import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Box, Typography, CircularProgress, Paper } from "@mui/material";

const PasswordApproval = () => {
  const location = useLocation();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState(
    "Processing your password approval request..."
  );

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

  useEffect(() => {
    const processPasswordApproval = async () => {
      try {
        // Extract URL parameters
        const urlParams = new URLSearchParams(location.search);
        const orgCode = urlParams.get("org_code");
        const appMode = urlParams.get("app_mode");
        const user = urlParams.get("user");
        const requestId = urlParams.get("request_id");

        console.log("Extracted parameters:", {
          orgCode,
          appMode,
          user,
          requestId,
        });

        if (!orgCode || !appMode || !user || !requestId) {
          setStatus("error");
          setMessage("Invalid approval link. Missing required parameters.");
          setLoading(false);
          return;
        }

        // Send API request
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}password_change_approval/`,
          {
            request_id: requestId,
            user: user,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "Org-Code": orgCode,
              "App-Mode": appMode,
            },
          }
        );

        console.log("API Response:", response.data);

        const apiStatus = String(response.data?.status || "").toLowerCase();
        let apiMessage = response.data?.message;
        if (apiMessage === "Invalid RequestID.") {
          apiMessage = "Request ID is expired. Please try again with a new request";
        }

        if (
          apiStatus === "success" ||
          (apiMessage && apiMessage.toLowerCase().includes("password approved"))
        ) {
          setStatus("success");
          setMessage(
            "Password has been reset successfully! You can now login with your new password."
          );
          toast.success("Password reset successfully!");
        } else if (apiStatus === "failed") {
          setStatus("failed");
          setMessage(
            apiMessage || "Password approval failed. Please contact support."
          );
          toast.error(apiMessage || "Password approval failed.");
        } else {
          setStatus("error");
          setMessage(
            apiMessage || "Failed to approve password. Please try again."
          );
          toast.error(apiMessage || "Failed to approve password.");
        }
      } catch (error) {
        console.error("Password approval error:", error);
        setStatus("error");

        if (error.response?.status === 400) {
          const serverMsgRaw = error.response?.data?.message;
          const serverMsg =
            serverMsgRaw === "Invalid RequestID." 
              ? "Request ID is expired. Please try again with a new request."
              : serverMsgRaw;
          setMessage(serverMsg || "Invalid request. Please check the approval link.");
        } else if (error.response?.status === 404) {
          setMessage("Approval request not found. The link may have expired.");
        } else if (error.response?.status === 500) {
          setMessage("Server error. Please try again later.");
        } else {
          setMessage("An unexpected error occurred. Please try again later.");
        }

        toast.error("Failed to process password approval.");
      } finally {
        setLoading(false);
      }
    };

    processPasswordApproval();
  }, [location.search]);

  const handleRedirectToLogin = () => {
    history.push("/login");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        backgroundImage: "url('/newImages/login_background.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "40%",
          minWidth: "400px",
          textAlign: "center",
          borderRadius: "10px",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h2"
            component="div"
            sx={{
              fontSize: "4rem",
              color: "#1d364d",
              fontWeight: "bold",
              marginBottom: 2,
            }}
          >
            🔐
          </Typography>
        </Box>

        {/* <Typography variant="h4" component="h1" gutterBottom color="primary">
          Password Verification
        </Typography> */}
        <div className="main-title">Password Verification</div>

        {loading ? (
          <Box sx={{ mt: 3 }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {message}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="h6"
              color={status === "success" ? "success.main" : "error.main"}
              sx={{ mb: 2 }}
            >
              {status === "success"
                ? "✓ Success"
                : status === "failed"
                ? "✗ Failed"
                : "✗ Error"}
            </Typography>

            <Typography variant="body1" sx={{ mb: 3 }}>
              {message}
            </Typography>

            {status === "success" && (
              <button
                className="btn btn__secondary mt-10"
                onClick={handleRedirectToLogin}
                type="button"
              >
                Go to Login
              </button>
            )}

            {(status === "error" || status === "failed") && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="btn btn__secondary mt-10"
                  onClick={handleRedirectToLogin}
                  type="button"
                >
                  Go to Login
                </button>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PasswordApproval;
